import { Content } from "@/ui";
import { getRoute } from "@/functions/getRoute";
import unslug from "unslug";
import { simple_pages_helper } from "@/helpers/simple_pages_helper";
import { createTitle } from "@/functions/createTitle";
import { jobSearch } from "@/functions/jobSearch";
import _ from "lodash";
import sn from "@/snippets";

//Builder Components
import getDynamicPage from "@/hooks/getDynamicPage";
import getDynamicPages from "@/hooks/getDynamicPages";
import { getDynamicPageStaticProps } from "@/functions/getDynamicPageStaticProps";
import { DynamicPageSeo } from "@/ui/DynamicPageSeo";
import { ContentBlocks } from "@/ui/ContentBlocks";

export default function Page({ content, prefix, title, seo, canonical, data, isBuilder = false }) {
  return (
    <>
      {isBuilder ? (
        <>
          <DynamicPageSeo
            {...seo}
            title={seo?.title.length > 0 ? seo.title : title}
            canonical={seo?.canonical.length > 0 ? seo.canonical : canonical}
          />
          <ContentBlocks data={data} prefix={prefix} />
        </>
      ) : (
        <Content items={content} />
      )}
    </>
  );
}

export async function getStaticProps({ params: { url_slugs } }) {
  // Adds Page Builder
  const builderSlug = url_slugs.join("/");
  const builderPage = getDynamicPage(builderSlug);
  if (builderPage) {
    return getDynamicPageStaticProps({
      page: builderPage,
      url_slug: builderSlug,
    });
  }

  const pages = url_slugs.map((url_slug, k) => ({
    url_slug,
    label: unslug(url_slug),
    href: getRoute("dynamic", { url_slugs: url_slugs.slice(0, k + 1) }),
  }));
  const [_page, prevPage] = [...pages].reverse();
  const page = simple_pages_helper.find(_page.url_slug);

  const parent = page.parent.id ? simple_pages_helper.find(page.parent.id, "id") : null;
  const siblings = page.parent.id ? simple_pages_helper.fetch({ parent: page.parent.id }) : [];
  const children = simple_pages_helper.fetch({ parent: page.id });

  const jobs = _.uniqBy(
    _.flatten(
      await Promise.all(
        (page?.show_related_jobs ?? []).map(
          async ({ query, location, radius }) =>
            await jobSearch({
              query,
              location,
              radius: radius ? parseInt(radius) : null,
            }),
        ),
      ),
    ),
    "id",
  );

  if (!page) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      meta: {
        title: createTitle(...pages.map((i) => i.label).reverse()),
      },
      content: [
        {
          component: "BreadcrumbNavigation",
          props: {
            items: pages,
          },
        },
        sn.header({ page, prevPage }),
        ...(page.show_sidebar_navigation
          ? [
              {
                component: "ContentWithSidebar",
                props: {
                  content: [
                    Array.isArray(page.carousel_items) && {
                      component: "LogoCarousel",
                      id: "LogoCarousel",
                      props: {
                        title: page.carousel_title ?? null,
                        items: (page.carousel_items ?? []).map((i) => ({
                          title: i["title"],
                          img: i["image"] ?? null,
                          href: i["link"] ?? "#",
                        })),
                        visibleCount: page.carousel_items_per_slide ?? 1,
                      },
                    },
                    {
                      component: "RichText",
                      id: "Intro",
                      props: {
                        className: "py-4 py-md-5",
                        body: page.intro ?? null,
                      },
                    },
                    Array.isArray(page.grid_buttons) && {
                      component: "GridButtonsGrid",
                      id: "GridButtonsGrid",
                      props: {
                        title: page.grid_buttons_title ?? null,
                        items: page.grid_buttons.map((i) => ({
                          title: i["title"],
                          img: i["image"] ?? null,
                          href: i["link"] ?? "#",
                        })),
                      },
                    },

                    // START - New Components
                    sn.features({ page }),
                    sn.featureTabs({ page }),
                    sn.benefits({ page }),
                    sn.trendingJobs({ page }),
                    sn.tileGroups({ page }),
                    sn.promotionalCarousel({ page }),
                    sn.multiTiles({ page }),
                    sn.multiCards({ page }),
                    sn.statCounterWidget({ page }),
                    // END - New Components

                    Array.isArray(page.collapsible_section_items) && {
                      component: "CollapsibleSection",
                      id: "CollapsibleSection",
                      props: {
                        title: page.collapsible_section_title ?? null,
                        description: page.collapsible_section_description ?? null,
                        items: page.collapsible_section_items.map((i) => ({
                          title: i["title"] ?? null,
                          body: i["body"] ?? null,
                        })),
                      },
                    },
                    {
                      component: "RichText",
                      id: "Body",
                      props: {
                        body: page.body ?? null,
                        ctas: Array.isArray(page.buttons)
                          ? page.buttons.map((i) => ({
                              label: i["label"] ?? "Read more",
                              href: i["link"] ?? "#",
                              variant: i["variant"] ?? "secondary",
                            }))
                          : [],
                      },
                    },
                    ...(Array.isArray(page.narrative_panels)
                      ? page.narrative_panels.map((i, k) => ({
                          component: "NarrativePanel",
                          id: `NarrativePanel-${k}`,
                          props: {
                            className: `py-4 py-md-5 bg-${i["background_color"] ?? "white"} text-${
                              i["text_color"] ?? "dark"
                            }`,
                            title: i["title"],
                            description: i["description"],
                            img: i["image"] ?? null,
                            cta: {
                              label: i["button_label"] ?? "Read more",
                              href: i["button_link"] ?? "#",
                              variant: i["button_variant"] ?? "secondary",
                            },
                            reverse: i["reverse"] ?? false,
                          },
                        }))
                      : []),
                    ...(Array.isArray(page.form)
                      ? page.form.map((i, k) => ({
                          component: "Form",
                          id: `Form-${k}`,
                          props: {
                            className: k % 2 === 0 ? "py-4 py-md-5 bg-primary" : "py-4 py-md-5 bg-primary",
                            title: i["title"],
                            description: i["description"],
                            formId: i["form_id"],
                          },
                        }))
                      : []),
                    Array.isArray(page.promo_section) && {
                      component: "PromoSection",
                      id: "PromoSection",
                      props: {
                        title: page.promo_section_title ?? "",
                        titleClass: page.url_slug === "employers" ? "text-tertiary" : "",
                        items: page.promo_section.map((i) => ({
                          title: i["title"],
                          img: i["image"] ?? null,
                          href: i["link"] ?? "#",
                        })),
                      },
                    },
                    Array.isArray(jobs) &&
                      jobs.length > 0 && {
                        component: "LatestJobs",
                        props: {
                          className: "py-5",
                          title: {
                            path: `page.${url_slugs.join(".")}.component.LatestJobs.title`,
                            placeholder: "Latest Jobs",
                          },
                          items: jobs,
                          visibleCount: 4,
                          button: true,
                        },
                      },
                  ],
                  sidebar: [
                    ...(page.show_sidebar_navigation
                      ? [
                          {
                            component: "SidebarNavigation",
                            props: {
                              items: parent
                                ? [
                                    {
                                      label: parent.title,
                                      href: prevPage.href,
                                      children: siblings.map(({ id, title, url_slug }) => ({
                                        label: title,
                                        href: getRoute("dynamic", {
                                          url_slugs: [...prevPage.href.split("/"), url_slug],
                                        }),
                                        active: id === page.id,
                                      })),
                                    },
                                  ]
                                : [
                                    {
                                      label: page["title"],
                                      href: _page.href,
                                      children: children.map(({ title, url_slug }) => ({
                                        label: title,
                                        href: getRoute("dynamic", { url_slugs: [...url_slugs, url_slug] }),
                                      })),
                                    },
                                  ],
                            },
                          },
                        ]
                      : []),
                  ],
                },
              },
            ]
          : [
              Array.isArray(page.carousel_items) && {
                component: "LogoCarousel",
                id: "LogoCarousel",
                props: {
                  title: page.carousel_title ?? null,
                  items: (page.carousel_items ?? []).map((i) => ({
                    title: i["title"],
                    img: i["image"] ?? null,
                    href: i["link"] ?? "#",
                  })),
                  visibleCount: page.carousel_items_per_slide ?? 1,
                },
              },
              {
                component: "RichText",
                id: "Intro",
                props: {
                  className: "py-4 py-md-5",
                  body: page.intro ?? null,
                },
              },
              Array.isArray(page.grid_buttons) && {
                component: "GridButtonsGrid",
                id: "GridButtonsGrid",
                props: {
                  title: page.grid_buttons_title ?? null,
                  items: page.grid_buttons.map((i) => ({
                    title: i["title"],
                    img: i["image"] ?? null,
                    href: i["link"] ?? "#",
                  })),
                },
              },

              // START - New Components
              sn.features({ page }),
              sn.featureTabs({ page }),
              sn.benefits({ page }),
              sn.trendingJobs({ page }),
              sn.tileGroups({ page }),
              sn.promotionalCarousel({ page }),
              sn.multiTiles({ page }),
              sn.multiCards({ page }),
              sn.statCounterWidget({ page }),
              // END - New Components

              Array.isArray(page.collapsible_section_items) && {
                component: "CollapsibleSection",
                id: "CollapsibleSection",
                props: {
                  title: page.collapsible_section_title ?? null,
                  description: page.collapsible_section_description ?? null,
                  items: page.collapsible_section_items.map((i) => ({
                    title: i["title"] ?? null,
                    body: i["body"] ?? null,
                  })),
                },
              },
              {
                component: "RichText",
                id: "Body",
                props: {
                  body: page.body ?? null,
                  ctas: Array.isArray(page.buttons)
                    ? page.buttons.map((i) => ({
                        label: i["label"] ?? "Read more",
                        href: i["link"] ?? "#",
                        variant: i["variant"] ?? "secondary",
                      }))
                    : [],
                },
              },
              ...(Array.isArray(page.narrative_panels)
                ? page.narrative_panels.map((i, k) => ({
                    component: "NarrativePanel",
                    id: `NarrativePanel-${k}`,
                    props: {
                      className: `py-4 py-md-5 bg-${i["background_color"] ?? "white"} text-${
                        i["text_color"] ?? "dark"
                      }`,
                      title: i["title"],
                      description: i["description"],
                      img: i["image"] ?? null,
                      cta: {
                        label: i["button_label"] ?? "Read more",
                        href: i["button_link"] ?? "#",
                        variant: i["button_variant"] ?? "secondary",
                      },
                      reverse: i["reverse"] ?? false,
                    },
                  }))
                : []),
              ...(Array.isArray(page.form)
                ? page.form.map((i, k) => ({
                    component: "Form",
                    id: `Form-${k}`,
                    props: {
                      className: k % 2 === 0 ? "py-4 py-md-5 bg-primary" : "py-4 py-md-5 bg-primary",
                      title: i["title"],
                      description: i["description"],
                      formId: i["form_id"],
                    },
                  }))
                : []),
              Array.isArray(page.promo_section) && {
                component: "PromoSection",
                id: "PromoSection",
                props: {
                  title: page.promo_section_title ?? "",
                  titleClass: page.url_slug === "employers" ? "text-tertiary" : "",
                  items: page.promo_section.map((i) => ({
                    title: i["title"],
                    img: i["image"] ?? null,
                    href: i["link"] ?? "#",
                  })),
                },
              },
              Array.isArray(jobs) &&
                jobs.length > 0 && {
                  component: "LatestJobs",
                  props: {
                    className: "py-5",
                    title: {
                      path: `page.${url_slugs.join(".")}.component.LatestJobs.title`,
                      placeholder: "Latest Jobs",
                    },
                    items: jobs,
                    visibleCount: 4,
                    button: true,
                  },
                },
            ]),
      ],
    },
  };
}

export async function getStaticPaths() {
  const builderPaths = getDynamicPages()
    .filter((i) => !["/"].includes(i.url_slug))
    .map((i) => ({ params: { url_slugs: i.url_slug.split("/") } }));

  return {
    paths: [
      ...builderPaths,
      ...simple_pages_helper.toNestedPaths({
        exclude: [
          "85f6911a-cf57-4bee-8aea-17d83d54bf06", // Homepage
          "2509b8a1-248e-4312-a824-9d15b16ea179", // Contact
          "e6a256b6-8ab2-46f0-9b89-b4e1800f0065", // Virtual Interviewing Tips
          "82af58c4-0769-43c5-a383-feff3fd184de", // Specialisms
        ],
      }),
    ],
    fallback: false,
  };
}
